import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  clearItemAisle,
  createStore,
  deleteStore,
  getAisles,
  itemKeys,
  renameStore,
  setAisleOrder,
  setItemAisle,
  type AisleCatalogue,
} from './itemsApi.ts';
import { isAisle, orderAisles, type Aisle } from './aisleLabels.ts';
import { normaliseItemName } from './normaliseItemName.ts';
import { isRetryable } from '../../offline/isOfflineError.ts';
import { enqueue } from '../../offline/mutationQueue.ts';

/**
 * Aisles, from the client's side.
 *
 * <b>Almost all of this feature is not here.</b> Which aisle a name belongs in is answered
 * by `aisleFor`, off a table shipped in the bundle, with no network and no waiting. What
 * this hook fetches is only the exceptions — the places a household has overruled that
 * table — plus the order it walks its aisles. On most groups that is an empty list and
 * eight strings.
 *
 * Which is why the query is keyed by group rather than by list, and why one key holds both:
 * a correction is about a name, every list in the household shares it, and a client holding
 * the corrections without a store cannot draw a single section.
 *
 * The stores come back in full — all of the household's, not the one in use. Which shop
 * somebody is standing in is a preference on their device that the server never hears
 * about, so it has nothing to filter by; and the switcher needs the rest anyway.
 */
export function useAisles(groupId: string | undefined) {
  const query = useQuery({
    queryKey: itemKeys.aisles(groupId ?? ''),
    queryFn: () => getAisles(groupId!),
    enabled: Boolean(groupId),
    // The hub invalidates this the moment anyone in the household corrects anything, so
    // there is nothing to be gained by refetching it on every focus.
    staleTime: 5 * 60_000,
  });

  const { data } = query;

  // Shaped for lookup rather than for the wire. `resolveAisle` is called once per row on
  // every render of the list, so it wants a Map, and building one per render would undo
  // the point of having it.
  const corrections = useMemo(() => correctionsOf(data), [data]);

  // Every order goes through `orderAisles`, which is what stops a bundle older than the
  // vocabulary drawing a nameless heading and a newer one losing a section the stored
  // order predates.
  const stores = useMemo<Store[]>(
    () =>
      (data?.stores ?? []).map((store) => ({
        id: store.id,
        name: store.name,
        order: orderAisles(store.order),
      })),
    [data],
  );

  /**
   * The order to draw the list in.
   *
   * Falls back through the first store and then the canonical order, so a remembered id
   * for a shop somebody else has since deleted still draws a list rather than nothing —
   * the same self-healing `useSelectedStore` does with the preference itself.
   */
  const orderFor = useMemo(
    () => (storeId: string | undefined) =>
      stores.find((store) => store.id === storeId)?.order ?? stores[0]?.order ?? orderAisles(undefined),
    [stores],
  );

  return { ...query, corrections, stores, orderFor };
}

/** One of the household's shops, with its order narrowed to the vocabulary we know. */
export interface Store {
  id: string;
  name: string;
  order: Aisle[];
}

function correctionsOf(catalogue: AisleCatalogue | undefined): ReadonlyMap<string, Aisle> {
  const corrections = new Map<string, Aisle>();

  for (const correction of catalogue?.corrections ?? []) {
    // Narrowed rather than cast. Everything the server sends comes out of the same closed
    // vocabulary, so this should never drop anything — but a code this bundle has never
    // heard of would otherwise draw a section with no heading on a screen somebody is
    // trying to shop from, and falling back to the shipped table is the better failure.
    if (isAisle(correction.aisle)) {
      corrections.set(correction.name, correction.aisle);
    }
  }

  return corrections;
}

export function invalidateAisles(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: itemKeys.allAisles });
}

/**
 * Correcting where a name lives.
 *
 * Applied on the tap rather than on collapse, for the reasons the photo is: it belongs to
 * the <i>name</i> and the card above it can change that name, so deferring would race the
 * two inside one commit; and it is a different endpoint and key space, so it cannot ride
 * inside the update the way the assignment does.
 *
 * The optimistic write is what makes the row move under the thumb that moved it. Without
 * it the section change waits for a round trip, which on a phone in a shop is the whole
 * difference between the app being quick and the app being broken.
 */
export function useSetAisle(groupId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, aisle }: { listId: string; itemId: string; name: string; aisle: Aisle }) =>
      setItemAisle(itemId, aisle),

    onMutate: ({ name, aisle }) => applyLocally(queryClient, groupId, name, aisle),

    onError: async (error, { listId, itemId, aisle }, rollback) => {
      if (isRetryable(error)) {
        await enqueue({ kind: 'setAisle', listId, itemId, aisle });
        return;
      }

      // The server refused, so the optimistic move was wrong. Put it back rather than
      // leaving the row somewhere nobody asked for.
      rollback?.();
      throw error;
    },

    onSettled: () => invalidateAisles(queryClient),
  });
}

/** Undoes a correction, putting the name back under the shipped table. */
export function useClearAisle(groupId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId }: { listId: string; itemId: string; name: string }) =>
      clearItemAisle(itemId),

    onMutate: ({ name }) => applyLocally(queryClient, groupId, name, undefined),

    onError: async (error, { listId, itemId }, rollback) => {
      if (isRetryable(error)) {
        await enqueue({ kind: 'clearAisle', listId, itemId });
        return;
      }

      rollback?.();
      throw error;
    },

    onSettled: () => invalidateAisles(queryClient),
  });
}

/**
 * Reordering one shop's aisles.
 *
 * No offline queue. It is done from a settings panel rather than from the list, so nothing
 * is waiting on it visually; and a reorder queued offline would replay against whatever the
 * vocabulary looked like by then, which is precisely the case the server's cleaning rules
 * exist to survive rather than to invite.
 */
export function useSetAisleOrder(groupId: string | undefined, storeId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (order: Aisle[]) => setAisleOrder(storeId!, order),

    // Written into the cache so the row moves on the tap that moved it. Without this the
    // list snaps back to where it was until the refetch lands, and a control that undoes
    // itself for a moment reads as one that did not work.
    onMutate: (order) => {
      if (!groupId || !storeId) {
        return undefined;
      }

      const key = itemKeys.aisles(groupId);
      const previous = queryClient.getQueryData<AisleCatalogue>(key);

      if (!previous) {
        return undefined;
      }

      // Only the shop being arranged. Rewriting the whole array is what would let a
      // reorder of Aldi appear, for a frame, to have rearranged Tesco as well.
      queryClient.setQueryData<AisleCatalogue>(key, {
        ...previous,
        stores: previous.stores.map((store) =>
          store.id === storeId ? { ...store, order } : store,
        ),
      });

      return () => queryClient.setQueryData<AisleCatalogue>(key, previous);
    },

    onError: (_error, _order, rollback) => rollback?.(),

    onSettled: () => invalidateAisles(queryClient),
  });
}

/**
 * Adding, renaming and removing shops.
 *
 * All three answer with the whole catalogue, so the cache is written from the response
 * rather than guessed at — there is no optimistic write here for the reason the reorder's
 * exists: these are done from a settings panel with nothing on the list waiting on them,
 * and a created store has a server-minted id that an optimistic row could not know.
 */
export function useCreateStore(groupId: string | undefined) {
  return useCatalogueMutation(groupId, (name: string) => createStore(groupId!, name));
}

export function useRenameStore(groupId: string | undefined) {
  return useCatalogueMutation(groupId, ({ storeId, name }: { storeId: string; name: string }) =>
    renameStore(storeId, name),
  );
}

export function useDeleteStore(groupId: string | undefined) {
  return useCatalogueMutation(groupId, (storeId: string) => deleteStore(storeId));
}

function useCatalogueMutation<TVariables>(
  groupId: string | undefined,
  mutationFn: (variables: TVariables) => Promise<AisleCatalogue>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,

    // The response *is* the new catalogue, so writing it saves the refetch an invalidation
    // would cost. The invalidation still follows, because the hub has told everyone else to
    // re-read and this client should agree with them rather than with its own last reply.
    onSuccess: (catalogue) => {
      if (groupId) {
        queryClient.setQueryData<AisleCatalogue>(itemKeys.aisles(groupId), catalogue);
      }
    },

    onSettled: () => invalidateAisles(queryClient),
  });
}

/**
 * Writes a correction straight into the cached catalogue, and hands back the undo.
 *
 * Editing the query data rather than setting component state: the catalogue is what every
 * row is drawn from, so this is the one place that has to change for the row to move — and
 * a second copy in a component could disagree with it.
 */
function applyLocally(
  queryClient: QueryClient,
  groupId: string | undefined,
  name: string,
  aisle: Aisle | undefined,
): (() => void) | undefined {
  if (!groupId) {
    return undefined;
  }

  const key = itemKeys.aisles(groupId);
  const previous = queryClient.getQueryData<AisleCatalogue>(key);

  if (!previous) {
    return undefined;
  }

  // The server files corrections under the normalised name, so the optimistic entry has to
  // use the same key or `resolveAisle` will not find it and the row will not move.
  const normalised = normaliseItemName(name);
  const others = previous.corrections.filter((correction) => correction.name !== normalised);

  queryClient.setQueryData<AisleCatalogue>(key, {
    ...previous,
    corrections: aisle ? [...others, { name: normalised, aisle }] : others,
  });

  return () => queryClient.setQueryData<AisleCatalogue>(key, previous);
}
